import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AbsencesPageHeader } from '@/features/absences/components/absences-page-header';
import { CreateAbsenceRequestDialog } from '@/features/absences/components/create-absence-request-dialog';
import { useDepartments } from '@/hooks/use-organizations';
import {
  getCalendarEvents,
  type CalendarAbsenceTypeInfo,
  type CalendarEvent,
  type CalendarEventType,
} from '@/lib/absences';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { absenceFillStyle } from '@/lib/absence-chip-style';
import { EVENT_TYPE_FALLBACK_COLOR } from '@/lib/calendar-event-colors';
import { cn } from '@/lib/utils';
import { parseDateOnly } from '@/lib/date';
import { useAuthStore } from '@/stores/auth';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { hasPlaceholderPermission } from '@/lib/permissions';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  isWeekend,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import {
  Cake,
  ChevronLeft,
  ChevronRight,
  Clock,
  LogIn,
  LogOut,
  Palmtree,
  Plus,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';

export const Route = createFileRoute('/absences/calendar')({
  beforeLoad: async () => {
    const { isAuthenticated, isInitialized } = useAuthStore.getState();
    if (!isInitialized) return;
    if (!isAuthenticated) throw redirect({ to: '/login' });

    const user = useAuthStore.getState().user;
    if (hasPlaceholderPermission(user)) throw redirect({ to: '/placeholder' });
  },
  component: AbsencesCalendarPage,
});

function AbsencesCalendarPage() {
  return (
    <AuthGuard>
      <AppLayout>
        <AbsencesPageHeader />
        <div className="min-h-full animate-fade-in bg-gradient-to-br from-background via-background to-muted/20 px-4 pb-4 pt-6">
          <FuturisticCalendar />
        </div>
      </AppLayout>
    </AuthGuard>
  );
}

const EVENT_TYPE_CONFIG: Record<
  CalendarEventType,
  { label: string; icon: typeof Cake; gradient: string; color: string }
> = {
  birthday: {
    label: 'Birthday',
    icon: Cake,
    gradient: 'from-pink-500 to-rose-500',
    color: EVENT_TYPE_FALLBACK_COLOR.birthday,
  },
  leave_request: {
    label: 'Leave',
    icon: Palmtree,
    gradient: 'from-emerald-500 to-teal-500',
    color: EVENT_TYPE_FALLBACK_COLOR.leave_request,
  },
  first_day: {
    label: 'First Day',
    icon: LogIn,
    gradient: 'from-blue-500 to-cyan-500',
    color: EVENT_TYPE_FALLBACK_COLOR.first_day,
  },
  last_day: {
    label: 'Last Day',
    icon: LogOut,
    gradient: 'from-orange-500 to-red-500',
    color: EVENT_TYPE_FALLBACK_COLOR.last_day,
  },
  last_probation_day: {
    label: 'Probation End',
    icon: Clock,
    gradient: 'from-amber-500 to-yellow-500',
    color: EVENT_TYPE_FALLBACK_COLOR.last_probation_day,
  },
};

function FuturisticCalendar() {
  const formatName = useEmployeeNameFormatter();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<CalendarEventType | 'all'>('all');
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isEggShaking, setIsEggShaking] = useState(false);
  const [showFallingAvatars, setShowFallingAvatars] = useState(false);

  // Calculate date range for the month view
  const dateRange = useMemo(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return {
      start: startOfWeek(start, { weekStartsOn: 1 }),
      end: endOfWeek(end, { weekStartsOn: 1 }),
    };
  }, [currentDate]);

  // Fetch departments for filter
  const { data: departments } = useDepartments();

  // Fetch calendar events from PeopleForce (includes absence types)
  const { data: calendarData, isLoading } = useQuery({
    queryKey: [
      'calendar-events',
      format(dateRange.start, 'yyyy-MM-dd'),
      format(dateRange.end, 'yyyy-MM-dd'),
      departmentFilter,
      typeFilter,
    ],
    queryFn: () =>
      getCalendarEvents({
        startDate: format(dateRange.start, 'yyyy-MM-dd'),
        endDate: format(dateRange.end, 'yyyy-MM-dd'),
        departmentId: departmentFilter !== 'all' ? departmentFilter : undefined,
        types: typeFilter !== 'all' ? [typeFilter] : undefined,
      }),
    staleTime: 0,
  });

  // Extract events and absence types from response
  const events = calendarData?.events;
  const absenceTypes = calendarData?.absenceTypes || [];

  // People on leave for the selected (or today's) date — for egg easter egg
  const eggTargetDate = selectedDate ?? new Date();
  const eggLeaveEvents = useMemo(() => {
    if (!events) return [];
    const target = startOfDay(eggTargetDate);
    return events.filter(
      (e) =>
        e.type === 'leave_request' &&
        // HW-701: pending leave is shown on the grid but doesn't count as away.
        e.status !== 'pending' &&
        isWithinInterval(target, {
          start: startOfDay(parseDateOnly(e.startsOn)),
          end: startOfDay(parseDateOnly(e.endsOn)),
        })
    );
  }, [events, eggTargetDate]);

  // Get events for selected date
  const selectedDateEvents = useMemo(() => {
    if (!events || !selectedDate) return [];
    const selected = startOfDay(selectedDate);
    return events.filter((e) =>
      isWithinInterval(selected, {
        start: startOfDay(parseDateOnly(e.startsOn)),
        end: startOfDay(parseDateOnly(e.endsOn)),
      })
    );
  }, [events, selectedDate]);

  // Generate days for calendar grid
  const days = useMemo(() => {
    return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
  }, [dateRange]);

  // Group events by day for quick lookup
  const eventsByDay = useMemo(() => {
    if (!events) return new Map<string, CalendarEvent[]>();
    const map = new Map<string, CalendarEvent[]>();

    events.forEach((event) => {
      const startDate = startOfDay(parseDateOnly(event.startsOn));
      const endDate = startOfDay(parseDateOnly(event.endsOn));

      // Add event to each day it spans
      days.forEach((day) => {
        const dayStart = startOfDay(day);
        if (isWithinInterval(dayStart, { start: startDate, end: endDate })) {
          const key = format(day, 'yyyy-MM-dd');
          const existing = map.get(key) || [];
          if (!existing.find((e) => e.id === event.id)) {
            map.set(key, [...existing, event]);
          }
        }
      });
    });

    return map;
  }, [events, days]);

  return (
    <div className="flex h-full flex-col gap-3">
      {/* Compact Header with Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            className="h-8 w-8 rounded-full"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="min-w-[140px] text-center text-lg font-semibold">
            {format(currentDate, 'MMMM yyyy')}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentDate(addMonths(currentDate, 1))}
            className="h-8 w-8 rounded-full"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setCurrentDate(new Date());
              setSelectedDate(new Date());
            }}
            className="ml-1 h-8"
          >
            Today
          </Button>
        </div>
      </div>

      {/* Today's Summary and Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex items-center gap-3 rounded-lg border s-honey-bg-soft px-4 py-2.5">
            <Users className="h-5 w-5 s-honey-text" />
            <span className="text-sm font-medium s-honey-text">
              {eggLeaveEvents.length}{' '}
              {eggLeaveEvents.length === 1 ? 'person' : 'people'} on leave
            </span>
            <svg
              className={cn(
                'ml-1 h-6 w-6 shrink-0 cursor-pointer s-honey-text',
                isEggShaking && 'animate-egg-shake'
              )}
              viewBox="0 0 512 512"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => {
                if (eggLeaveEvents.length === 0) return;
                setIsEggShaking(true);
                setTimeout(() => setIsEggShaking(false), 500);
                setShowFallingAvatars(true);
                setTimeout(() => setShowFallingAvatars(false), 2200);
              }}
            >
              <path
                d="M256,0C170.813,0,65.938,120.242,65.938,283.149C65.938,446.062,162.906,512,256,512 c93.09,0,190.062-65.938,190.062-228.851C446.062,120.242,341.184,0,256,0z"
                fill="currentColor"
                className="opacity-100"
              />
            </svg>
            {showFallingAvatars && eggLeaveEvents.length > 0 && (
              <FallingAvatars events={eggLeaveEvents} />
            )}
          </div>

          <div className="flex items-center gap-2">
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="h-8 w-[150px] text-xs">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments?.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={typeFilter}
              onValueChange={(v) => setTypeFilter(v as CalendarEventType | 'all')}
            >
              <SelectTrigger className="h-8 w-[130px] text-xs">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {Object.entries(EVENT_TYPE_CONFIG).map(([type, config]) => (
                  <SelectItem key={type} value={type}>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: config.color }}
                      />
                      {config.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Create Absence Request Dialog */}
        <CreateAbsenceRequestDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          initialDate={selectedDate}
        />
      </div>

      {/* Main Calendar Grid */}
      <div className="grid gap-4 xl:grid-cols-[1fr_280px]">
        {/* Calendar */}
        <Card className="overflow-hidden rounded-2xl border shadow-lg">
          <CardContent className="p-4">
            {isLoading ? (
              <CalendarSkeleton />
            ) : (
              <>
                {/* Day Headers */}
                <div className="mb-2 grid grid-cols-7 gap-1">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                    <div
                      key={day}
                      className="py-1 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {days.map((day) => {
                    const dayKey = format(day, 'yyyy-MM-dd');
                    const dayEvents = eventsByDay.get(dayKey) || [];
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isTodayDate = isToday(day);
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    const isWeekendDay = isWeekend(day);

                    return (
                      <TooltipProvider key={dayKey}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => setSelectedDate(day)}
                              className={cn(
                                'group relative flex min-h-[120px] w-full flex-col items-start justify-start rounded-lg p-2 transition-all',
                                'hover:bg-hygge/10',
                                !isCurrentMonth && 'opacity-30',
                                isWeekendDay && 'bg-muted/60',
                                isSelected && 'ring-2 ring-hygge bg-hygge/10',
                                isTodayDate && !isSelected && 'bg-hygge/5'
                              )}
                            >
                              {/* Date Number */}
                              <span
                                className={cn(
                                  'mb-1 flex h-6 w-6 items-center justify-center rounded text-xs font-semibold',
                                  isTodayDate && 'bg-cta text-cta-foreground',
                                  !isTodayDate &&
                                    isSelected &&
                                    's-sage-text',
                                  !isTodayDate &&
                                    !isSelected &&
                                    isWeekendDay &&
                                    'text-muted-foreground/50'
                                )}
                              >
                                {format(day, 'd')}
                              </span>

                              {/* Event Icons */}
                              {dayEvents.length > 0 && (
                                <div className="mt-auto flex w-full flex-wrap items-center justify-start gap-1.5 px-0.5">
                                  {dayEvents.slice(0, 8).map((event, idx) => {
                                    const config = EVENT_TYPE_CONFIG[event.type];
                                    const Icon = config.icon;
                                    const color = event.absenceTypeColor || config.color;
                                    // HW-701: pending → outline (no fill), approved → solid fill.
                                    const isPending = event.status === 'pending';
                                    const fill = absenceFillStyle(color, isPending);
                                    return (
                                      <div
                                        key={`${event.id}-${idx}`}
                                        className={cn(
                                          'flex h-4 w-4 items-center justify-center rounded-md',
                                          !isPending && 'shadow-sm',
                                          fill.className
                                        )}
                                        style={fill.style}
                                      >
                                        <Icon className="h-2.5 w-2.5" />
                                      </div>
                                    );
                                  })}
                                  {dayEvents.length > 8 && (
                                    <span className="text-[9px] font-semibold text-muted-foreground">
                                      +{dayEvents.length - 8}
                                    </span>
                                  )}
                                </div>
                              )}
                            </button>
                          </TooltipTrigger>
                          {dayEvents.length > 0 && (
                            <TooltipContent side="bottom" className="max-w-[220px]">
                              <p className="font-semibold">{format(day, 'EEEE, MMM d')}</p>
                              <div className="mt-1.5 space-y-1">
                                {dayEvents.slice(0, 5).map((event) => {
                                  const config = EVENT_TYPE_CONFIG[event.type];
                                  const Icon = config.icon;
                                  const color = event.absenceTypeColor || config.color;
                                  const isPending = event.status === 'pending';
                                  const fill = absenceFillStyle(color, isPending);
                                  return (
                                    <div
                                      key={event.id}
                                      className="flex items-center gap-1.5 text-xs"
                                    >
                                      <div
                                        className={cn(
                                          'flex h-4 w-4 items-center justify-center rounded',
                                          fill.className
                                        )}
                                        style={fill.style}
                                      >
                                        <Icon className="h-2.5 w-2.5" />
                                      </div>
                                      <span className="truncate">
                                        {formatName({
                                          firstName: event.employeeFirstName ?? '',
                                          lastName: event.employeeLastName ?? '',
                                          preferredName: event.employeePreferredName,
                                        })}
                                      </span>
                                      <span className="text-background/60">·</span>
                                      <span className="truncate text-background/75">
                                        {event.absenceTypeName}
                                      </span>
                                      {isPending && (
                                        <span className="shrink-0 text-background/60">
                                          · Pending
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                                {dayEvents.length > 5 && (
                                  <p className="text-xs text-background/75">
                                    +{dayEvents.length - 5} more
                                  </p>
                                )}
                              </div>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Selected Day Panel */}
        <div className="space-y-3">
          <Card className="rounded-2xl border shadow-lg">
            <CardContent className="p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">
                  {selectedDate ? format(selectedDate, 'EEE, MMM d') : 'Select a date'}
                </h3>
                {selectedDate && (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          className="h-7 w-7 shrink-0 rounded-full shadow-sm"
                          onClick={() => setCreateDialogOpen(true)}
                          aria-label="Request absence for this day"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="left">Request absence for this day</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {selectedDate && (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {selectedDateEvents.length === 0 ? (
                    <div className="flex items-center gap-3 py-4 text-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full s-sage-bg-soft">
                        <Palmtree className="h-5 w-5 s-sage-text" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium s-sage-text">
                          No events
                        </p>
                        <p className="text-xs text-muted-foreground">Everyone&apos;s available!</p>
                      </div>
                    </div>
                  ) : (
                    selectedDateEvents.map((event) => (
                      <EventCard key={event.id} event={event} />
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Legend */}
          <Legend absenceTypes={absenceTypes} />
        </div>
      </div>
    </div>
  );
}

/** Avatar initials are the person's identity marker, so they stay first+last
 * regardless of the display-order setting or a preferredName (HW-729). */
function employeeInitials(firstName: string | null, lastName: string | null): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

function FallingAvatars({ events }: { events: CalendarEvent[] }) {
  const positions = useMemo(() => {
    return events.map((_, i) => {
      const spread = events.length > 1 ? (i / (events.length - 1)) * 140 - 70 : 0;
      const rise = -120 - i * 20;
      return { x: spread, y: rise };
    });
  }, [events]);

  return (
    <div className="pointer-events-none absolute right-6 top-1/2 h-0 w-0 -translate-y-1/2 overflow-visible">
      {events.map((event, i) => (
        <div
          key={`${event.id}-${i}`}
          className="absolute animate-fall-out"
          style={
            {
              '--fall-x': `${positions[i]?.x ?? 0}px`,
              '--fall-y': `${positions[i]?.y ?? 0}px`,
              animationDelay: `${i * 80}ms`,
            } as React.CSSProperties
          }
        >
          <Avatar className="h-10 w-10 ring-2 ring-amber-200 dark:ring-amber-800 shadow-lg">
            <AvatarImage src={event.employeeAvatar || undefined} />
            <AvatarFallback
              className="text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200"
              style={
                event.absenceTypeColor
                  ? { backgroundColor: `${event.absenceTypeColor}30`, color: event.absenceTypeColor }
                  : undefined
              }
            >
              {employeeInitials(event.employeeFirstName, event.employeeLastName)}
            </AvatarFallback>
          </Avatar>
        </div>
      ))}
    </div>
  );
}

function EventCard({ event }: { event: CalendarEvent }) {
  const formatName = useEmployeeNameFormatter();
  const config = EVENT_TYPE_CONFIG[event.type];
  const Icon = config.icon;
  const color = event.absenceTypeColor || config.color;
  const employeeName = formatName({
    firstName: event.employeeFirstName ?? '',
    lastName: event.employeeLastName ?? '',
    preferredName: event.employeePreferredName,
  });
  // HW-701: pending → outline (no fill) + a "Pending" tag; approved → solid.
  const isPending = event.status === 'pending';
  const fill = absenceFillStyle(color, isPending);

  const cardClassName =
    'group flex w-full items-center gap-2 rounded-lg border p-2 text-left transition-all hover:bg-muted/50';

  const content = (
    <>
      <div
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
          fill.className
        )}
        style={fill.style}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate text-sm font-medium">
          <span className="truncate">{employeeName}</span>
          {isPending && (
            <span
              className={cn(
                'shrink-0 rounded px-1 py-px text-[9px] font-semibold uppercase leading-none',
                fill.className
              )}
              style={fill.style}
            >
              Pending
            </span>
          )}
        </p>
        <p className="truncate text-[10px] text-muted-foreground">
          {event.absenceTypeName}
          {event.startsOn !== event.endsOn &&
            ` · ${format(parseDateOnly(event.startsOn), 'MMM d')} - ${format(parseDateOnly(event.endsOn), 'MMM d')}`}
        </p>
      </div>
      <Avatar className="h-6 w-6 shrink-0">
        <AvatarImage src={event.employeeAvatar || undefined} />
        <AvatarFallback className="text-[9px]" style={{ backgroundColor: `${color}20`, color }}>
          {employeeInitials(event.employeeFirstName, event.employeeLastName)}
        </AvatarFallback>
      </Avatar>
    </>
  );

  if (!event.employeeId) {
    return <div className={cardClassName}>{content}</div>;
  }

  return (
    <Link to="/employees/$id" params={{ id: event.employeeId }} className={cardClassName}>
      {content}
    </Link>
  );
}

function Legend({ absenceTypes }: { absenceTypes: CalendarAbsenceTypeInfo[] }) {
  const nonLeaveEventTypes = Object.entries(EVENT_TYPE_CONFIG).filter(
    ([type]) => type !== 'leave_request'
  );

  return (
    <Card className="rounded-2xl border">
      <CardContent className="p-3 space-y-3">
        {/* Absence Types (Leave Types) */}
        {absenceTypes.length > 0 && (
          <div>
            <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Leave Types
            </h3>
            <div className="flex flex-wrap gap-2">
              {absenceTypes.map((type) => (
                <div
                  key={type.id}
                  className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1"
                >
                  <div
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: type.color || '#6B7280' }}
                  />
                  <span className="text-xs">{type.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Other Event Types */}
        <div>
          <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Other
          </h3>
          <div className="flex flex-wrap gap-2">
            {nonLeaveEventTypes.map(([type, config]) => (
              <div
                key={type}
                className="flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1"
              >
                <div className="h-2 w-2 rounded-full" style={{ backgroundColor: config.color }} />
                <span className="text-xs">{config.label}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CalendarSkeleton() {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} className="h-4 rounded" />
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: 35 }).map((_, i) => (
          <Skeleton key={i} className="h-14 rounded-lg" />
        ))}
      </div>
    </div>
  );
}
